<template>
  <div
    class="rounded-lg p-2 text-left w-full max-w-[198px] mx-auto transition-colors"
    :class="toneClass"
  >
    <!-- Header: status dot + label -->
    <div class="flex items-center gap-1.5">
      <span
        class="inline-block w-2 h-2 rounded-full shrink-0"
        :class="dotClass"
      />
      <span class="text-[12px] font-semibold" :class="labelClass">
        {{ label }}
      </span>
    </div>

    <!-- Sub text: 繼承來源 / 覆寫說明 -->
    <div
      v-if="subText"
      class="mt-1 text-[11px] leading-snug text-slate-500 break-words"
    >
      {{ subText }}
    </div>

    <!-- Segmented 三態切換（對齊 crm_demo：淡灰底 container + 選中 pill 浮出 shadow） -->
    <div class="mt-2 inline-flex w-full rounded-md bg-slate-100 p-0.5 gap-0.5">
      <button
        v-for="opt in OPTIONS"
        :key="opt.value"
        type="button"
        :aria-pressed="mode === opt.value"
        :class="segBtnClass(opt)"
        :disabled="disabled"
        @click.stop="onClick(opt.value)"
      >
        {{ $t(opt.labelKey) }}
      </button>
    </div>
  </div>
</template>

<script>
const OPTIONS = [
  // btn 選中色只分「操作 / 破壞」兩類：inherit+grant 用 indigo 中性藍、revoke 用 rose 警示
  // wrap tone（見 TONE）負責傳達當前語意色（繼承允許=藍 / 個人允許=綠 / 個人撤銷=粉 / 未授權=白）
  { value: 'inherit', labelKey: 'permission_mode_inherit', onClass: 'bg-indigo-500 text-white shadow-sm' },
  { value: 'grant', labelKey: 'permission_mode_grant', onClass: 'bg-indigo-500 text-white shadow-sm' },
  { value: 'revoke', labelKey: 'permission_mode_revoke', onClass: 'bg-rose-500 text-white shadow-sm' }
]

// tone：四態視覺分層，色系跟 btn 對齊 —
// inherited-allow 用藍（繼承允許，常見）、override-grant 用綠（個人加碼）、
// override-revoke 用粉（個人撤銷）、none 用白底 slate ring（未授權）。
// override 的 ring 刻意用 200（比 inherit 的 100 更粗）讓個人異動跳出。
const TONE = {
  'inherited-allow': {
    wrap: 'bg-indigo-50 ring-1 ring-indigo-100',
    dot: 'bg-indigo-500',
    label: 'text-indigo-700'
  },
  'override-grant': {
    wrap: 'bg-emerald-50 ring-1 ring-emerald-200',
    dot: 'bg-emerald-500',
    label: 'text-emerald-700'
  },
  'override-revoke': {
    wrap: 'bg-rose-50 ring-1 ring-rose-200',
    dot: 'bg-rose-500',
    label: 'text-rose-700'
  },
  'none': {
    wrap: 'bg-white ring-1 ring-slate-200',
    dot: 'bg-slate-300',
    label: 'text-slate-400'
  }
}

export default {
  name: 'PermissionCellToggle',
  props: {
    mode: { type: String, default: 'inherit' },
    inheritedValue: { type: Boolean, default: false },
    label: { type: String, default: '' },
    subText: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return { OPTIONS }
  },
  computed: {
    tone () {
      if (this.mode === 'grant') return 'override-grant'
      if (this.mode === 'revoke') return 'override-revoke'
      return this.inheritedValue ? 'inherited-allow' : 'none'
    },
    toneClass () {
      return TONE[this.tone].wrap
    },
    dotClass () {
      return TONE[this.tone].dot
    },
    labelClass () {
      return TONE[this.tone].label
    }
  },
  methods: {
    segBtnClass (opt) {
      // 對齊 crm_demo：btn 間無邊線、rounded 獨立 pill、選中浮出帶 shadow；未選中透明底
      const base = 'flex-1 inline-flex items-center justify-center px-1.5 py-1 min-h-[22px] text-[11px] leading-none font-medium rounded cursor-pointer transition-all duration-200 focus:outline-none disabled:cursor-not-allowed'
      if (this.mode === opt.value) return `${base} ${opt.onClass}`
      return `${base} text-slate-500 hover:text-slate-700 hover:bg-white/70`
    },
    onClick (value) {
      if (this.disabled || value === this.mode) return
      this.$emit('change', value)
    }
  }
}
</script>
